/* eslint-disable */
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  getDashboardGraph,
  getEmailGraph,
  getPushGraph,
  getRecentMessages,
  getSmsGraph,
  getTopMaskNames,
  getTopTenSubs,
  getTotalInteg,
  getTotalMasks,
  getTotalMsgs,
  getTotalOrgs,
  getTotalTxnAndCol,
} from './actions';
import { getMode } from 'emessage/components/mode-switch';
import { getDatasets } from 'modules/data-management/actions';
import { orderBy } from 'lodash';
import { formatDate, formatNumber } from 'helper';

const type = {
  hour: 'DAILY',
  day: 'MONTHLY',
  month: 'YEARLY',
};

export const useTransactionHistogram = (filter) => {
  const params = useMemo(
    () => ({
      date_from: filter?.from,
      date_to: filter?.to,
      type: type[filter?.type],
    }),
    [filter],
  );

  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-dashboard-main-graph', params],
    queryFn: getDashboardGraph,
    retry: true,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  const { table, chart } = useMemo(() => {
    const tableData = (data || []).map((x) => ({
      id: x.label,
      name: x.label,
      messages: x.count,
    }));
    const chartData = (data || []).map((x) => ({
      id: x.label,
      title: x.label,
      count: x.count,
    }));
    return {
      table: tableData,
      chart: chartData,
    };
  }, [data]);

  if (getMode() === '1') {
    return [isLoading, table, chart];
  }
  return [
    isLoading && false,
    [
      { id: 1, name: 'Jan 2024', messages: '53,448,735' },
      { id: 2, name: 'Feb 2024', messages: '69,432,849' },
      { id: 3, name: 'Mar 2024', messages: '70,680,988' },
      { id: 4, name: 'Apr 2024', messages: '59,817,025' },
      { id: 5, name: 'May 2024', messages: '63,205,964' },
      { id: 6, name: 'Jun 2024', messages: '57,117,228' },
      { id: 7, name: 'Jul 2024', messages: '43,462,002' },
      { id: 8, name: 'Aug 2024', messages: '80,164,101' },
      { id: 9, name: 'Sep 2024', messages: '34,255,808' },
      { id: 9, name: 'Oct 2024', messages: '33,967,210' },
      { id: 9, name: 'Nov 2024', messages: '26,390,102' },
      { id: 9, name: 'Dec 2024', messages: '35,057,988' },
    ],
    [
      { id: 1, title: 'Jan 2024', count: 53448735 },
      { id: 2, title: 'Feb 2024', count: 69432849 },
      { id: 3, title: 'Mar 2024', count: 70680988 },
      { id: 4, title: 'Apr 2024', count: 59817025 },
      { id: 5, title: 'May 2024', count: 63205964 },
      { id: 6, title: 'Jun 2024', count: 57117228 },
      { id: 7, title: 'Jul 2024', count: 43462002 },
      { id: 8, title: 'Aug 2024', count: 80164101 },
      { id: 9, title: 'Sep 2024', count: 34255808 },
      { id: 9, title: 'Oct 2024', count: 33967210 },
      { id: 9, title: 'Nov 2024', count: 26390102 },
      { id: 9, title: 'Dec 2024', count: 35057988 },
    ],
    table,
    chart,
  ];
};

export const useDatasetGraph = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-graph', { q: 'graph-data' }],
    queryFn: getDatasets,
    retry: true,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  const { table, chart } = useMemo(() => {
    const tableData = orderBy(data || [], 'label', 'asc').map((x) => ({
      id: x.label,
      name: formatDate(x.label, 'MMM YYYY'),
      messages: formatNumber(x.value, 0),
    }));
    const chartData = orderBy(data || [], 'label', 'asc').map((x) => ({
      id: x.label,
      title: formatDate(x.label, 'MMM YYYY'),
      count: +x.value,
    }));
    return {
      table: tableData,
      chart: chartData,
    };
  }, [data]);

  return [isLoading, table, chart];
};

export const useHorizontalBar = (filter) => {
  const params = useMemo(
    () => ({
      date_from: filter?.from,
      date_to: filter?.to,
      type: type[filter?.type],
    }),
    [filter],
  );

  const { data: raw, isSubsLoading } = useQuery({
    queryKey: ['get-top-ten-subscribers', params],
    queryFn: getTopTenSubs,
    retry: false,
    keepPreviousData: false,
  });
  const { data: summaryRaw, isSummaryLoading } = useQuery({
    queryKey: ['get-summary-data'],
    queryFn: getTotalTxnAndCol,
    retry: false,
    keepPreviousData: false,
  });
  const isLoading = isSubsLoading || isSummaryLoading;
  const data = raw?.data?.data;
  const summaryData = summaryRaw?.data?.data;
  const { chart, summary } = useMemo(() => {
    const chartData = (data || []).map((x) => ({
      id: x.organization_name,
      title: x.organization_name,
      count: x.collections,
    }));
    return {
      chart: chartData,
      summary: {
        count: summaryData?.total_transaction,
        amount: summaryData?.total_collection,
      },
    };
  }, [data, summaryData]);
  return [isLoading, '', chart, '', summary];
};

export const useTop10Agencies = () => {
  const { data: raw, isSubsLoading } = useQuery({
    queryKey: ['dashboard-top-10-agency', { q: 'top-ten-agency' }],
    queryFn: getDatasets,
    retry: false,
    keepPreviousData: false,
  });
  const isLoading = isSubsLoading;
  const data = raw?.data?.data;
  const { chart, table, summary } = useMemo(() => {
    const chartData = (data || []).map((x) => ({
      id: x.label,
      title: x.label,
      count: +x.value || 0,
    }));
    return {
      chart: orderBy(chartData, 'count', 'desc'),
      table: orderBy(
        (data || []).map((x) => ({
          name: x.label,
          sort: +x.value || 0,
          messages: formatNumber(x.value || '0', 0),
        })),
        'sort',
        'desc',
      ),
      summary: {
        count: '?',
        amount: '-',
      },
    };
  }, [data]);
  return [isLoading, table, chart, '', summary];
};

export const usePieChart = (filter) => {
  const params = useMemo(
    () => ({
      date_from: filter?.from,
      date_to: filter?.to,
      type: type[filter?.type],
    }),
    [filter],
  );
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-top-mask-names', { ...params, size: 10 }],
    queryFn: getTopMaskNames,
    retry: false,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  return [isLoading, '', data];
};

export const useTop10SenderChart = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-top-10-sender', { q: 'top-ten-sender' }],
    queryFn: getDatasets,
    retry: false,
    keepPreviousData: false,
  });
  const data = orderBy(
    (raw?.data?.data || []).map((x) => ({
      mask_name: x.label,
      count: +x.value,
    })),
    'count',
    'desc',
  );
  return [isLoading, '', data];
};

export const useTopOrg = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-top-org', { q: 'top-ten-agency' }],
    queryFn: getDatasets,
    retry: false,
    keepPreviousData: false,
  });
  return [isLoading, raw?.data?.data];
};

export const useTotalMsgs = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-msgs'],
    queryFn: getTotalMsgs,
    retry: false,
    keepPreviousData: true,
  });
  const data = {
    total_messages: raw?.data?.data?.total_messages || 0,
  };
  return [isLoading, data];
};

export const useTotalOrgs = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-orgs'],
    queryFn: getTotalOrgs,
    retry: false,
    keepPreviousData: true,
  });
  const data = {
    total_organization: +raw?.data?.data?.total_organization,
  }; // raw?.data?.data;
  return [isLoading, data];
};

export const useTotalInteg = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-integrations'],
    queryFn: getTotalInteg,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useTotalMasks = () => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-masks'],
    queryFn: getTotalMasks,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

const toHistogramFormat = (data) => {
  const table = (data || []).map((x) => ({
    id: x.label,
    name: x.label,
    messages: formatNumber(x.count, 0),
  }));
  const chart = (data || []).map((x) => ({
    id: x.label,
    title: x.label,
    count: +x.count,
  }));
  return { table, chart };
};

const toGraphParams = (filter) => ({
  date_from: filter?.from,
  date_to: filter?.to,
  type: type[filter?.type],
});

export const useSmsUsageGraph = (filter) => {
  const params = useMemo(() => toGraphParams(filter), [filter]);
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-sms-graph', params],
    queryFn: getSmsGraph,
    retry: false,
    keepPreviousData: true,
  });
  const { table, chart } = useMemo(
    () => toHistogramFormat(raw?.data?.data),
    [raw],
  );
  return [isLoading, table, chart];
};

export const usePushUsageGraph = (filter) => {
  const params = useMemo(() => toGraphParams(filter), [filter]);
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-push-graph', params],
    queryFn: getPushGraph,
    retry: false,
    keepPreviousData: true,
  });
  const { table, chart } = useMemo(
    () => toHistogramFormat(raw?.data?.data),
    [raw],
  );
  return [isLoading, table, chart];
};

export const useEmailUsageGraph = (filter) => {
  const params = useMemo(() => toGraphParams(filter), [filter]);
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-email-graph', params],
    queryFn: getEmailGraph,
    retry: false,
    keepPreviousData: true,
  });
  const { table, chart } = useMemo(
    () => toHistogramFormat(raw?.data?.data),
    [raw],
  );
  return [isLoading, table, chart];
};

export const useAdminRecentMessages = (params) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['dashboard-recent-messages', params],
    queryFn: getRecentMessages,
    retry: false,
    keepPreviousData: true,
  });
  return [isLoading, raw?.data?.data || [], raw?.data?.meta || {}];
};
